/**
 * Navigation Component - Side navigation with categorized items
 * Based on AWS Console patterns (e.g., Amazon Bedrock)
 * @module components/starnexus/common/Navigation
 */

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SideNavigation, { SideNavigationProps } from '@amzn/awsui-components-console/side-navigation';
import Badge from '@amzn/awsui-components-console/badge';
import { useAuthStore } from '../../../stores/starnexus/authStore';
import { UserRole, LegacyUserRole } from '../../../types/starnexus/team';

// ============================================================================
// Navigation Item Configuration
// ============================================================================

interface NavItemConfig {
  type: 'link' | 'section' | 'divider' | 'section-group';
  text?: string;
  href?: string;
  items?: NavItemConfig[];
  roles?: (UserRole | LegacyUserRole)[];
  info?: React.ReactNode;
}

/**
 * Greyed "Coming soon" badge shown next to not-yet-launched services so they
 * read as disabled. Cloudscape SideNavigation has no native disabled-link state,
 * so we signal it via a muted info badge (links already point to '#', which
 * handleFollow ignores).
 */
const COMING_SOON = <Badge color="grey">Coming soon</Badge>;

/**
 * Navigation items organized by Portal architecture
 * Uses 'section' type for bold category headers (like Bedrock console)
 */
const NAV_ITEMS: NavItemConfig[] = [
  // Portal level
  { type: 'section', text: 'Portal',
    items: [
      { type: 'link', text: 'Home', href: '/starnexus' },
    ]
  },
  // StarUploader service module
  { type: 'section', text: 'StarUploader',
    items: [
      { type: 'link', text: 'Create dataset', href: '/starnexus/uploader/datasets/create' },
      { type: 'link', text: 'Upload snapshot', href: '/starnexus/uploader/datasets?action=upload-picker' },
      { type: 'link', text: 'Datasets', href: '/starnexus/uploader/datasets' },
      { type: 'link', text: 'Processing', href: '/starnexus/uploader/processing' },
    ]
  },
  // Cross-service modules
  { type: 'section', text: 'Andes',
    items: [
      { type: 'link', text: 'Subscriptions', href: '/starnexus/andes/subscriptions' },
    ]
  },
  { type: 'section', text: 'Admin',
    items: [
      { type: 'link', text: 'Dashboard', href: '/starnexus/dashboard', roles: ['Owner', 'manager', 'admin'] },
      { type: 'link', text: 'Team', href: '/starnexus/team', roles: ['Owner', 'Member', 'manager', 'admin'] },
      { type: 'link', text: 'Monitoring', href: '/starnexus/monitoring', roles: ['Owner', 'admin'] },
    ]
  },
  { type: 'section', text: 'Documentation',
    items: [
      { type: 'link', text: 'Getting started', href: '/starnexus/docs/getting-started' },
      { type: 'link', text: 'Upload & transform', href: '/starnexus/docs/schema-config' },
      { type: 'link', text: 'Andes publishing', href: '/starnexus/docs/andes-integration' },
    ]
  },
  { type: 'section', text: 'Settings',
    items: [{ type: 'link', text: 'Settings', href: '/starnexus/settings' }]
  },
  // Future services (coming soon) — greyed out / disabled until launched
  { type: 'section', text: 'More services',
    items: [
      { type: 'link', text: 'StarBridge', href: '#', info: COMING_SOON },
      { type: 'link', text: 'StarMailer', href: '#', info: COMING_SOON },
      { type: 'link', text: 'StarSwap', href: '#', info: COMING_SOON },
    ]
  },
];

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Filter navigation items based on user role
 */
function filterNavItemsByRole(items: NavItemConfig[], userRole: UserRole | LegacyUserRole | undefined): SideNavigationProps.Item[] {
  if (!userRole) return [];

  return items
    .filter((item) => {
      if (!item.roles) return true;
      return item.roles.includes(userRole);
    })
    .map((item) => {
      if (item.type === 'divider') {
        return { type: 'divider' } as SideNavigationProps.Item;
      }

      // Handle section-group (expandable sections with header)
      if (item.type === 'section-group' && item.items) {
        const filteredChildren = filterNavItemsByRole(item.items, userRole);
        // Only show section if it has visible children
        if (filteredChildren.length === 0) return null;

        return {
          type: 'expandable-link-group',
          text: item.text,
          href: '#',
          defaultExpanded: true,
          items: filteredChildren as SideNavigationProps.Link[]
        } as SideNavigationProps.Item;
      }

      if (item.type === 'section' && item.items) {
        const filteredChildren = filterNavItemsByRole(item.items, userRole);
        if (filteredChildren.length === 0) return null;

        return {
          type: 'section',
          text: item.text,
          items: filteredChildren as SideNavigationProps.Link[]
        } as SideNavigationProps.Item;
      }

      return {
        type: 'link',
        text: item.text,
        href: item.href,
        info: item.info
      } as SideNavigationProps.Item;
    })
    .filter((item): item is SideNavigationProps.Item => item !== null);
}

/**
 * Find the active href based on current location
 */
function findActiveHref(pathname: string): string {
  // Exact matches
  const exactMatches = [
    '/starnexus',
    '/starnexus/uploader/datasets',
    '/starnexus/uploader/datasets/create',
    '/starnexus/uploader/processing',
    '/starnexus/andes/explorer',
    '/starnexus/andes/permissions',
    '/starnexus/andes/subscriptions',
    '/starnexus/dashboard',
    '/starnexus/team',
    '/starnexus/monitoring',
    '/starnexus/settings',
    '/starnexus/docs/getting-started',
    '/starnexus/docs/schema-config',
    '/starnexus/docs/andes-integration'
  ];

  for (const route of exactMatches) {
    if (pathname === route) return route;
  }

  // Dataset detail pages -> highlight "Datasets"
  if (pathname.match(/^\/starnexus\/uploader\/datasets\/[^/]+$/)) {
    return '/starnexus/uploader/datasets';
  }

  // Doc pages fallback
  if (pathname.startsWith('/starnexus/docs/')) {
    return '/starnexus/docs/getting-started';
  }

  return '/starnexus';
}

// ============================================================================
// Navigation Component
// ============================================================================

export interface NavigationProps {
  onNavigate?: (href: string) => void;
}

export const Navigation: React.FC<NavigationProps> = ({ onNavigate }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const role = useAuthStore((state) => state.role);

  const filteredItems = React.useMemo(() => filterNavItemsByRole(NAV_ITEMS, role ?? undefined), [role]);

  const activeHref = React.useMemo(() => findActiveHref(location.pathname), [location.pathname]);

  const handleFollow: SideNavigationProps['onFollow'] = (event) => {
    event.preventDefault();
    const href = event.detail.href;
    if (href && href !== '#') {
      navigate(href);
      onNavigate?.(href);
    }
  };

  return (
    <SideNavigation
      activeHref={activeHref}
      header={{
        href: '/starnexus',
        text: 'StarNexus'
      }}
      items={filteredItems}
      onFollow={handleFollow}
    />
  );
};

export default Navigation;
